import { Box, BoxProps } from '@chakra-ui/react';
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CSSProperties, ReactNode } from 'react';

interface ReorderableItem {
  id: string;
}

interface ReorderGridProps<T extends ReorderableItem> {
  values: T[];
  onReorder: (newOrder: T[]) => void;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  renderOverlay?: (item: T) => ReactNode;
}

function ReorderGrid<T extends ReorderableItem>({
  values,
  onReorder,
  children,
  className,
  style,
  renderOverlay,
  ...props
}: ReorderGridProps<T> & BoxProps) {
  // Using the actual item while dragging (no DragOverlay)
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    }),
  );

  const handleDragStart = (_event: DragStartEvent) => {
    // no-op; we don't need overlay bookkeeping
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = values.findIndex((item) => item.id === active.id);
      const newIndex = values.findIndex((item) => item.id === over.id);

      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      onReorder(arrayMove(values, oldIndex, newIndex));
    }

    // nothing to clean up
  };

  const handleDragCancel = () => {};

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToParentElement]}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={values} strategy={rectSortingStrategy}>
        <Box
          className={className}
          style={{ position: 'relative', ...style }}
          {...props}
        >
          {children}
        </Box>
      </SortableContext>
    </DndContext>
  );
}

interface ReorderItemProps<T extends ReorderableItem> {
  value: T;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  dragControls?: boolean;
}

function ReorderItem<T extends ReorderableItem>({
  value,
  children,
  className,
  style,
  dragControls,
  ...props
}: ReorderItemProps<T> & BoxProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useSortable({ id: value.id });

  const sortableStyle = {
    transform: CSS.Transform.toString(transform),
    transition: undefined,
    willChange: isDragging ? 'transform' : undefined,
    opacity: isDragging ? 0.7 : 1,
    zIndex: isDragging ? 1000 : 'auto',
    cursor: isDragging ? 'grabbing' : undefined,
  };

  return (
    <Box
      ref={setNodeRef}
      className={className}
      style={{
        position: 'relative',
        ...sortableStyle,
        ...style,
      }}
      {...attributes}
      {...props}
      {...(!dragControls && { ...listeners })}
    >
      {children}
    </Box>
  );
}

export type { ReorderableItem, ReorderGridProps, ReorderItemProps };
export { ReorderGrid, ReorderItem };
